import type { Placement } from '@floating-ui/react-dom';
import { useRef, useEffect, ReactNode, lazy, Suspense } from 'react';

const Container = lazy(() => import('./Container'));

type Props = {
  /** ポップアップの内容が説明している予定を表している HTML 要素 */
  reference: HTMLElement;
  /** ポップアップの位置 */
  placement?: Placement;
  float?: boolean;
  children?: ReactNode;
  onRequestClose?: () => void;
};

function Popup({
  reference,
  placement,
  float,
  children,
  onRequestClose,
}: Props) {
  const { containerRef } = usePopupCloser(onRequestClose);

  return (
    <Suspense>
      <Container reference={reference} placement={placement} float={float}>
        <div ref={containerRef}>{children}</div>
      </Container>
    </Suspense>
  );
}

export default Popup;

/**
 * 指定した要素の子孫要素以外をクリックしたときポップアップを閉じるようにする
 * @param close ポップアップを閉じるために呼び出される関数
 */
export function usePopupCloser(close: (() => void) | undefined) {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!close) return;

    const listener = ({ target }: MouseEvent) => {
      if (!(target instanceof Node)) return;
      const container = containerRef.current;
      if (container && container.contains(target)) return;
      close();
    };

    window.addEventListener('click', listener, { capture: true });
    return () =>
      window.removeEventListener('click', listener, { capture: true });
  }, [close]);

  return {
    /** クリックでは閉じない要素の祖先要素 */
    containerRef,
  };
}
